import { theme } from '@zappyrent/hundop';
import { findLast, upperFirst } from 'lodash';
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToastContext } from '../../../contexts/ToastProvider';
import propertyAttachmentsAPI from '../../../services/api/property-attachments';
import awsS3API from '../../../services/api/s3upload';
import useStore from '../../../store';
import { fileToBuffer, sleep } from '../../bookings/utils';
import {
  StyledButton,
  StyledDeleteIcon,
  StyledDocumentUploadIcon,
  StyledInnerWrapper,
  StyledInput,
  StyledLoadingBar,
  StyledNav,
  StyledTitle,
  StyledDocumentDownloadIcon,
  StyledWrapper,
  StyledReloadIcon,
  StyledSpinner,
} from './PropertyAttachmentsSingleFileHandlerField.styles';
import {
  IPropertyAttachmentDocument,
  IPropertyAttachmentFieldProps,
} from './PropertyAttachmentsSingleFileHandlerField.types';

const PropertyAttachmentsSingleFileHandlerField = ({
  title,
  type,
  property_id,
  setApeDocument,
}: IPropertyAttachmentFieldProps): JSX.Element => {
  const { t } = useTranslation(['a11y', 'translation', 'form']);
  const toast = useToastContext();

  const user = useStore(state => state.user);
  const userType = user?.type;

  const fileInput = useRef<HTMLInputElement | null>(null);
  const deleteButtonRef = useRef(
    null,
  ) as MutableRefObject<HTMLButtonElement | null>;

  const [documentUploaded, setDocument] =
    useState<IPropertyAttachmentDocument | null>(null);

  const [isUploaded, setIsUploaded] = useState<boolean>(
    Boolean(documentUploaded && documentUploaded.type === type),
  );
  const [loadingProgressPercentage, setLoadingProgressPercentage] = useState<
    null | number
  >(isUploaded ? 100 : null);

  const [getUploadedFileLoading, setUploadedFileLoading] = useState(false);
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const [isDeletePending, setIsDeletePending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const documentFieldTitle = useMemo(() => {
    if (isDeletePending) return t('form:confirmDelete');
    else if (isUploadFailed) return t('form:uploadFailed');
    else if (
      loadingProgressPercentage !== null &&
      !isUploaded &&
      !isUploadFailed
    )
      return t('form:loading');
    else if (isDeleting) return t('form:deleting');
    else return title;
  }, [
    isDeletePending,
    isUploadFailed,
    loadingProgressPercentage,
    isUploaded,
    isDeleting,
  ]);

  const handleOnDeleteButtonClick = async () => {
    try {
      if (!isDeletePending) {
        setIsDeletePending(true);
        return;
      } else {
        setIsDeletePending(false);
        setIsDeleting(true);
      }

      if (documentUploaded?.id) {
        await propertyAttachmentsAPI.deletePropertyAttachment({
          propertyId: property_id,
          id: documentUploaded.id.toString(),
        });
      }
      fileInput.current!.value = '';
      setLoadingProgressPercentage(null);
      isUploaded && setDocument(null);
      setIsUploaded(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOnUploadButtonClick = () => {
    if (isUploaded) return;
    fileInput?.current?.click();
  };

  const handleOnViewButtonClick = async () => {
    const attachmentLink =
      await propertyAttachmentsAPI.getPropertyAttachmentURL({
        propertyId: property_id,
        propertyAttachmentId: documentUploaded!.id!,
      });
    if (attachmentLink === undefined) return;

    window.open(attachmentLink, '_blank');
  };

  const handleOnInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const file: File = (e.target.files as FileList)[0];
    if (!file) return;
    const { size: fileSize, type: fileType } = file;
    const fileExtension = file.name.split('.').slice(-1)[0];
    const newFilename = `${type}.${fileExtension}`;
    const [credentials, fileBuffer] = await Promise.all([
      awsS3API.getAWSCredentials(),
      fileToBuffer(file),
    ]);
    if (
      fileType !== 'image/jpeg' &&
      fileType !== 'image/png' &&
      fileType !== 'application/pdf'
    ) {
      toast.error(t('translation:uploadedImageFormatIsNotValid'));
      fileInput.current!.value = '';
      return;
    }

    if (fileSize >= 5000000) {
      toast.warning(t('translation:uploadedImageIsTooBig'));
      fileInput.current!.value = '';
      return;
    }

    try {
      setLoadingProgressPercentage(0);

      await awsS3API
        .uploadObjectToS3Bucket(
          credentials,
          {
            Bucket: process.env.REACT_APP_PROPERTY_ATTACHMENTS_BUCKET!,
            Key: `${property_id}/${newFilename}`,
            Body: fileBuffer,
            Metadata: {
              type: type,
              filename: newFilename,
            },
          },
          setLoadingProgressPercentage,
        )
        .then(async () => {
          await getUploadedFile();
        });
    } catch (err) {
      setIsUploadFailed(true);
      console.error(err);
    }
    setUploadedFileLoading(false);
    setLoadingProgressPercentage(null);
  };

  const getUploadedFile = async () => {
    try {
      setUploadedFileLoading(true);
      await sleep(1500); // wait for the file to be uploaded to S3 and attachment to be created
      let attachments =
        await propertyAttachmentsAPI.getPropertyAttachmentByType({
          propertyId: property_id,
          type: type,
        });
      let retryCount = 0;
      while (attachments?.length <= 0 && retryCount < 10) {
        await sleep(500); // Wait for 500 second before retrying
        attachments = await propertyAttachmentsAPI.getPropertyAttachmentByType({
          propertyId: property_id,
          type: type,
        });
        retryCount++;
      }
      const foundDocument = findLast(attachments, { type: type }) as
        | IPropertyAttachmentDocument
        | undefined;
      if (foundDocument) {
        setDocument(foundDocument);
        setIsUploaded(true);
      } else {
        setIsUploadFailed(true);
        setIsUploaded(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUploadedFileLoading(false);
      setLoadingProgressPercentage(null);
    }
  };

  const handleResetButtonClick = () => {
    setIsUploadFailed(false);
    setLoadingProgressPercentage(null);
    fileInput.current!.value = '';
  };

  const fetchDocument = async () => {
    try {
      setUploadedFileLoading(true);
      const response = await propertyAttachmentsAPI.getPropertyAttachmentByType(
        {
          propertyId: property_id,
          type: type,
        },
      );
      const foundDocument = findLast(response, { type: type }) as
        | IPropertyAttachmentDocument
        | undefined;
      if (foundDocument) {
        setIsUploaded(true);
        setDocument(foundDocument);
      } else {
        setIsUploaded(false);
        setDocument(null);
        setApeDocument && setApeDocument(null);
      }
    } catch (error) {
      toast.error(t('Unable to fetch document'));
      console.error('Fetch error:', error);
    } finally {
      setUploadedFileLoading(false);
    }
  };

  useEffect(() => {
    fetchDocument();
  }, []);

  useEffect(() => {
    const callback = (e: MouseEvent) => {
      if ((e.target as HTMLButtonElement) === deleteButtonRef.current) return;
      if (isDeletePending) setIsDeletePending(false);
    };
    document.addEventListener('click', callback);

    return () => {
      document.removeEventListener('click', callback);
    };
  });

  useEffect(() => {
    if (setApeDocument) {
      setApeDocument(documentUploaded);
    }
  }, [documentUploaded]);

  return (
    <StyledWrapper
      loadingProgressPercentage={loadingProgressPercentage}
      isUploaded={isUploaded}
      isUploadFailed={isUploadFailed}
      isDeleting={isDeletePending || isDeleting}
      user={userType}
    >
      <StyledInnerWrapper>
        <StyledTitle
          isUploadFailed={isUploadFailed}
          isDeleting={isDeletePending || isDeleting}
        >
          {upperFirst(documentFieldTitle)}
        </StyledTitle>
        <br />
        <StyledNav>
          {isUploadFailed && (
            <StyledButton
              onClick={handleResetButtonClick}
              title={t('a11y:addDocument')}
            >
              <StyledReloadIcon />
            </StyledButton>
          )}{' '}
          <br />
          {getUploadedFileLoading && (
            <StyledSpinner
              size={20}
              foregroundColor={theme.colors.landlord.main}
            />
          )}{' '}
          <br />
          {!isUploadFailed && (
            <>
              {!isUploaded && (
                <StyledButton
                  onClick={handleOnUploadButtonClick}
                  title={t('a11y:addDocument')}
                  type="button"
                >
                  <StyledDocumentUploadIcon
                    isLoading={loadingProgressPercentage !== null}
                    user={userType}
                  />
                </StyledButton>
              )}
              {isUploaded && (
                <>
                  <StyledButton
                    onClick={handleOnDeleteButtonClick}
                    title={t('a11y:deleteDocument')}
                    ref={deleteButtonRef}
                    type="button"
                  >
                    <StyledDeleteIcon />
                  </StyledButton>
                  <StyledButton
                    onClick={handleOnViewButtonClick}
                    title={t('a11y:viewDocument')}
                    type="button"
                  >
                    <StyledDocumentDownloadIcon />
                  </StyledButton>
                </>
              )}
            </>
          )}
        </StyledNav>
        <StyledInput
          accept="image/jpeg, image/png, application/pdf"
          id={property_id.toString()}
          ref={fileInput}
          type="file"
          onChange={e => {
            handleOnInputChange(e);
          }}
        />
        <StyledLoadingBar
          isUploadFailed={isUploadFailed}
          loadingProgressPercentage={loadingProgressPercentage}
          isUploaded={isUploaded}
          isDeleting={isDeletePending || isDeleting}
          user={userType}
        />
      </StyledInnerWrapper>
    </StyledWrapper>
  );
};

export default PropertyAttachmentsSingleFileHandlerField;
